import type {
  FileRecord,
  FolderListing,
  SearchResultItem,
  SessionInfo,
} from '@setone-sounds/shared/types';

import { apiFetch } from './api';

/** Login success payload (mirrors shared schema). */
export interface LoginResponse {
  ok: true;
  user: { username: string };
}

export type SessionResponse = SessionInfo;

export interface FolderChild {
  name: string;
  path: string;
  fileCount: number;
}

export interface FoldersResponse {
  path: string;
  folders: FolderChild[];
}

export interface SearchResponse {
  q: string;
  results: SearchResultItem[];
}

export interface TextPreviewResponse {
  id: string;
  filename: string;
  encoding: string;
  truncated: boolean;
  byteLength: number;
  maxBytes: number;
  text: string;
}

export const authApi = {
  session: () => apiFetch<SessionResponse>('/api/auth/session'),
  login: (username: string, password: string) =>
    apiFetch<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: { username, password },
    }),
  logout: () =>
    apiFetch<{ ok: true }>('/api/auth/logout', {
      method: 'POST',
    }),
};

export const libraryApi = {
  folders: (parentPath = '') => {
    const qs = new URLSearchParams();
    if (parentPath) qs.set('parentPath', parentPath);
    const q = qs.toString();
    return apiFetch<FoldersResponse>(`/api/folders${q ? `?${q}` : ''}`);
  },
  files: (parentPath = '', cursor?: string, limit = 100) => {
    const qs = new URLSearchParams();
    if (parentPath) qs.set('parentPath', parentPath);
    if (cursor) qs.set('cursor', cursor);
    qs.set('limit', String(limit));
    return apiFetch<FolderListing>(`/api/files?${qs}`);
  },
  file: (id: string) => apiFetch<FileRecord>(`/api/files/${id}`),
  text: (id: string) => apiFetch<TextPreviewResponse>(`/api/files/${id}/text`),
};

export const searchApi = {
  files: (q: string, limit = 10_000) => {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    return apiFetch<SearchResponse>(`/api/search?${qs}`);
  },
  ai: (q: string, limit = 500) =>
    apiFetch<SearchResponse>('/api/search/semantic', {
      method: 'POST',
      body: { q, limit },
    }),
};
